import {
  PlusOutlined,
  SearchOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  Dropdown,
  Input,
  Menu,
  message,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from 'antd';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { useEffect, useRef, useState, useCallback } from 'react';
import { CSVLink } from 'react-csv';
import { Link, useNavigate } from 'react-router-dom';
import { ReactComponent as Archiveicon } from '../../Images/Archiveicon.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { font } from '../../Common/font';
import logo from '../../Images/alux-logo.png';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';
import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { ReactComponent as PlanPrintIcon } from '../../Images/SimplePrintIcon.svg';
import { ReactComponent as PrintIcon } from '../../Images/PrintIcon.svg';
import { ReactComponent as ViewIcon } from '../../Images/ViewIcon.svg';
import { ReactComponent as Archive } from '../../Images/archive.svg';
import {
  deleteDieQuotation,
  fetchDieQuotationList,
} from '../../slices/die-quotation';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import moment from 'moment';
import { fetchAndSetIpAddress } from '../../slices/ipSlice';
import { createActivityLog } from '../../slices/activity-log';
import eventBus from '../../utils/eventBus';

function DieQuotationList() {
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const { user: currentUser } = useSelector(state => state.auth);
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(
    localStorage.getItem('DieQuotationList') || '',
  );
  const [inputValue, setInputValue] = useState(
    localStorage.getItem('DieQuotationList') || '',
  );

  const [dropdownVisible, setDropdownVisible] = useState(false);

  const [pagesize, setPageSize] = useState(20);
  const {
    data = [],
    totalRecord,
    status,
  } = useSelector(state => state.dieQuotation);

  const ipAddress = useSelector(state => state.ip.ipAddress);
  useEffect(() => {
    if (!ipAddress) {
      dispatch(fetchAndSetIpAddress());
    }
  }, [ipAddress, dispatch]);

  const fetchDieQuotation = useCallback(() => {
    dispatch(
      fetchDieQuotationList({
        page,
        pagesize,
        ...(search && { search: search }),
        ...(orderBy && { ordering: orderBy }),
      }),
    );
  }, [dispatch, page, pagesize, search, orderBy]);

  useEffect(() => {
    fetchDieQuotation();
  }, [fetchDieQuotation]);

  useEffect(() => {
    const handleReload = () => {
      fetchDieQuotation();
    };

    eventBus.on("financialYear:refresh", handleReload);

    return () => {
      eventBus.off("financialYear:refresh", handleReload);
    };
  }, [fetchDieQuotation]);

  const handleEdit = record => {
    navigate(`/edit-die-quotation/${record.id}`);
  };

  const handleView = record => {
    navigate(`/view-die-quotation-list/${record.id}`);
  };

  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
  }));

  const debounceTimeout = useRef(null);

  const handleSearch = e => {
    const value = e.target.value;
    setInputValue(value);
    setSearch(value);
    localStorage.setItem('DieQuotationList', value);
  };
  const resetFilter = () => {
    setSearch('');
    setInputValue('');
    setOrderBy(null); // if applicable

    localStorage.removeItem('DieQuotationList');
    dispatch(fetchDieQuotationList({ page: 1, pagesize }));
  };
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  const handleDelete = id => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(deleteDieQuotation(id))
      .unwrap()
      .then(() => {
        message.success('Die Quotation Archive Successfully!');
        dispatch(
          fetchDieQuotationList({
            page,
            pagesize,
            ...(search && { search: search }),
            ...(orderBy && { ordering: orderBy }),
          }),
        );
      })
      .catch(error => {
        if (typeof error === 'object' && error !== null) {
          const messages = [];
          for (const key in error) {
            if (Array.isArray(error[key])) {
              messages.push(`${key}: ${error[key].join(', ')}`);
            }
          }
          if (messages.length > 0) {
            message.error(messages.join('; '));
          } else {
            message.error('An unknown error occurred.');
          }
        } else if (typeof error === 'string') {
          message.error(error); // Handle string error
        } else if (error instanceof Error) {
          message.error(error.message); // Handle Error object
        } else {
          message.error('An unknown error occurred.');
        }
      });
  };

  const [checkedList, setCheckedList] = useState([
    'SrNo',
    'die_quotation_details',
    'quotation_date',
    'die_quotation_no',
    'press_capacity',
    'quantity',
    'profile_devlopment_cost',
    'cgst',
    'sgst',
    'name',
    'total',
    'customer',
    'action',
  ]);

  const columns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      width: 100,
      render: (text, record, index) => {
        return (page - 1) * pagesize + index + 1;
      },
    },
    {
      title: 'Quotation Date',
      dataIndex: 'quotation_date',
      key: 'quotation_date',
      sorter: true,
      render: quotation_date => {
        return (
          <span>
            {quotation_date
              ? dayjs(quotation_date).format('DD-MM-YYYY')
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Quotation No.',
      dataIndex: 'die_quotation_no',
      key: 'die_quotation_no',
      sorter: true,
      render: die_quotation_no => {
        return <span>{die_quotation_no ? die_quotation_no : 'N/A'}</span>;
      },
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer',
      key: 'customer',
      sorter: true,
      render: customer => {
        return (
          <span>
            {customer?.customer_name ? customer.customer_name : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',

      render: record => {
        return record ? (
          <span>{dayjs(record).format('DD-MM-YYYY HH:mm')}</span>
        ) : (
          <span>N/A</span>
        );
      },
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      sorter: true,
      render: record => {
        return (
          <span>
            {record ? `${record.first_name} ${record.last_name}` : 'N/A'}{' '}
          </span>
        );
      },
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: record => {
        return (
          <span>
            {record && record.trim()
              ? dayjs(record).format('DD-MM-YYYY HH:mm')
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Updated By',
      dataIndex: 'updated_by',
      key: 'updated_by',
      sorter: true,
      render: record => {
        return (
          <span>
            {record && record.first_name?.trim() && record.last_name?.trim()
              ? `${record.first_name} ${record.last_name}`
              : 'N/A'}
          </span>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'action',
      render: record => (
        <Space>
          {currentUser.permissions.permissions.die_quotation.includes(
            'view_diequotation',
          ) && (
              <Tooltip placement="top" title={'View'}>
                <span
                  onClick={() => handleView(record)}
                  style={{ cursor: 'pointer' }}
                >
                  <ViewIcon />
                </span>
              </Tooltip>
            )}
          {currentUser.permissions.permissions.die_quotation.includes(
            'print_die_quotation_pdf_copy',
          ) && (
              <Tooltip title="Print">
                <Link
                  to={`/print-die-quotation/${record.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PlanPrintIcon style={{ cursor: 'pointer' }} />
                </Link>
              </Tooltip>
            )}

          {currentUser.permissions.permissions.die_quotation.includes(
            'change_diequotation',
          ) && (
              <Tooltip placement="top" title={'Edit'}>
                <span
                  onClick={() => handleEdit(record)}
                  style={{ cursor: 'pointer' }}
                >
                  <EditIcon />
                </span>
              </Tooltip>
            )}
          {currentUser.permissions.permissions.die_quotation.includes(
            'delete_diequotation',
          ) && (
              <Tooltip placement="top" title={'Archive'}>
                <Popconfirm
                  title="Are you sure you want to archive this record?"
                  onConfirm={() => handleDelete(record.id)} // Correctly access `id`
                  onCancel={() => message.info('Action cancelled')}
                  okText="Yes"
                  cancelText="No"
                >
                  <Archive style={{ cursor: 'pointer' }} />
                </Popconfirm>
              </Tooltip>
            )}
        </Space>
      ),
    },
  ];

  const handleColumnVisibilityChange = checkedValues => {
    setCheckedList(checkedValues);
  };

  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item key="columnMenu">
        <Checkbox.Group
          value={checkedList}
          onChange={checkedValues => {
            handleColumnVisibilityChange(checkedValues);
          }}
        >
          <Row gutter={16}>
            <Col xs={12} md={12}>
              <Checkbox value="SrNo" disabled>
                Sr.No.
              </Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="quotation_date">Quotation Date</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="die_quotation_no">Quotation No</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="customer">Customer Name</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_at">Created At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="created_by">Created By</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_at">Updated At</Checkbox>
            </Col>
            <Col xs={24} md={12}>
              <Checkbox value="updated_by">Updated By</Checkbox>
            </Col>
            <Col xs={12} md={12}>
              <Checkbox value="action" disabled>
                Action
              </Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );

  const csvheaders = [
    { label: 'Sr. No', key: 'SrNo' },
    { label: 'Quotation Date', key: 'quotation_date' },
    { label: 'Quotation No', key: 'die_quotation_no' },
    { label: 'Customer Name', key: 'customer' },
    { label: 'created At', key: 'created_at' },
    { label: 'created By', key: 'created_by' },
    { label: 'Updated At', key: 'updated_at' },
    { label: 'Updated By', key: 'updated_by' },
  ];

  const csvFilterheaders = csvheaders.filter(column =>
    checkedList.includes(column.key),
  );

  const csvData = data.map((item, index) => ({
    SrNo: index + 1, // Add serial number dynamically
    quotation_date: item.quotation_date
      ? moment(item.quotation_date).format('DD-MM-YYYY')
      : 'N/A',
    die_quotation_no: item.die_quotation_no,
    customer: item?.customer?.customer_name
      ? item?.customer?.customer_name
      : 'N/A',
    created_at: item.created_at
      ? moment(item.created_at).format('DD-MM-YYYY')
      : 'N/A',
    created_by: item.created_by
      ? `${item.created_by.first_name} ${item.created_by.last_name}`
      : 'N/A',
    updated_at: item.updated_at
      ? moment(item.updated_at).format('DD-MM-YYYY')
      : 'N/A',
    updated_by: item.updated_by
      ? `${item.updated_by.first_name} ${item.updated_by.last_name}`
      : 'N/A',
  }));

  const handlePrintDownload = async record => {
    /* PDF Generation */
    const doc = new jsPDF('p', 'pt', 'a4');
    doc.addFileToVFS('WorkSans-normal.ttf', font);
    doc.addFont('WorkSans-normal.ttf', 'WorkSans', 'normal');
    doc.setFont('WorkSans');
    const pageHeight = doc.internal.pageSize.height;
    let y = 20;
    const imgUrl = logo; // Replace with your image
    const imgWidth = 181; // Adjust width as needed
    const imgHeight = 48; // Adjust height as needed
    const margin = 10;
    const imgX = doc.internal.pageSize.width / 2 + 100;
    const imgY = margin;

    doc.addImage(imgUrl, 'JPEG', imgX, imgY, imgWidth, imgHeight);
    y += 55;

    doc.setFontSize(22);
    doc.text('Die Quotation', doc.internal.pageSize.width / 2, y, {
      align: 'center',
    });
    y += 60;

    doc.setFontSize(12);
    const leftMargin = 0;
    const labelWidth = 20;

    let currentY = y;

    const leftFields = [
      { label: 'M/S.', value: '' },
      { label: '', value: `${record?.customer?.customer_name}` || 'N/A' },
      {
        label: '',
        value:
          `${record?.customer?.office_address?.shop}, ${record?.customer?.office_address?.area}` ||
          'N/A',
      },
      {
        label: '',
        value:
          `${record?.customer?.office_address?.landmark}, ${record?.customer?.office_address?.area}` ||
          'N/A',
      },
      {
        label: '',
        value:
          `${record?.customer?.office_address?.city}, ${record?.customer?.office_address?.state} - ${record?.customer?.office_address?.pin_code}` ||
          'N/A',
      },
      { label: '', value: '' },
      { label: '', value: '' },
      { label: '', value: '' },
      { label: 'Kind Attention', value: '' },
      {
        label: 'Name:',
        value:
          record?.customer?.customer_contact_persons[0]?.contact_person_name ||
          'N/A',
      },
      {
        label: 'Mobile:',
        value:
          `+${record?.customer?.customer_contact_persons[0]?.contact_person_mobile_number}` ||
          'N/A',
      },
    ];

    const rightFields = [
      {
        label: 'Date: ',
        value: dayjs(record?.quotation_date).format('DD-MM-YYYY'),
      },
      { label: 'Quotation No: ', value: record?.die_quotation_no || 'N/A' },
      { label: '', value: '' },
      { label: '', value: '' },
      { label: '', value: '' },
      { label: '', value: '' },
    ];

    const lineHeight = 15;

    const leftFieldsX = 8;
    const rightFieldsX = doc.internal.pageSize.width / 2 + 50;

    let leftFieldsY = y;
    let rightFieldsY = y;

    leftFields.forEach(term => {
      const isSectionHeader =
        term.label.trim() === 'M/S.' || term.label.trim() === 'Kind Attention';

      if (isSectionHeader) {
        doc.setFillColor(2, 96, 187); // Blue background
        doc.setTextColor(255, 255, 255); // White text
        doc.rect(leftFieldsX - 2, leftFieldsY - 12, 120, lineHeight, 'F'); // 'F' = fill only
        doc.setFont('WorkSans', 'bold');
        doc.text(term.label, leftFieldsX, leftFieldsY);
        doc.setFont('WorkSans', 'normal');
        doc.setTextColor(0, 0, 0);
      } else if (term.label.trim()) {
        doc.setFont('WorkSans', 'bold');
        doc.text(term.label, leftFieldsX, leftFieldsY);
        doc.setFont('WorkSans', 'normal');
        doc.text(term.value, leftFieldsX + 50, leftFieldsY);
      } else {
        doc.setFont('WorkSans', 'normal');
        doc.text(term.value, leftFieldsX, leftFieldsY);
      }

      leftFieldsY += 15;
    });

    currentY = y;
    const rightColX = doc.internal.pageSize.width / 2 + 150;

    rightFields.forEach(field => {
      if (field.label === 'Kind Attention') {
        doc.setFont('WorkSans', 'bold');
        doc.setFillColor(2, 96, 187); // Black background
        doc.setTextColor(255, 255, 255);
        doc.rect(570, currentY - 12, 200, 17, 'F'); // Adjust width/height as needed
      } else {
        doc.setFont('WorkSans', 'normal');
        doc.setTextColor(0, 0, 0);
      }
      doc.text(field.label, rightColX - 70, currentY);
      doc.text(field.value, rightColX + 30, currentY);
      currentY += lineHeight;
    });

    y = currentY + 70;
    const pdfColumns = ['Sr. No.'];
    const mappedColumns = [(item, index) => index + 1];

    const bodyRows = record?.die_quotation_details.map((item, index) => {
      return [
        index + 1,
        `${item?.inquiry_number ? item.inquiry_number : 'N/A'}`,
        item?.profile_no?.die_number || '',
        `${item.profile_no?.dimension1 || ''} MM` +
        (item.profile_no?.dimension2
          ? ` X ${item.profile_no?.dimension2} MM`
          : '') +
        (item.profile_no?.dimension3
          ? ` X ${item.profile_no?.dimension3} MM`
          : ''),
        `${item?.alloy?.name || ''}`,
        item?.press_capacity,
        item?.unit_of_measurement,
        item?.quantity,
        item?.profile_devlopment_cost,
        item?.gst_amount,
        item?.gst_amount,
        item?.total_amount,
      ];
    });
    bodyRows.push([
      {
        content: 'Total :',
        colSpan: 7,
        styles: { halign: 'center', fontStyle: 'bold' },
      },
      { content: record?.summary?.total_quantity, styles: { fontStyle: 'bold' } },
      {
        content: record?.summary?.total_basic_amount,
        styles: { fontStyle: 'bold' },
      },
      { content: record?.summary?.total_gst_amount, styles: { fontStyle: 'bold' } },
      { content: record?.summary?.total_gst_amount, styles: { fontStyle: 'bold' } },
      {
        content: record?.summary?.total_amount_with_gst,
        styles: { fontStyle: 'bold' },
      },
    ]);
    const tableResult = doc.autoTable({
      head: [
        [
          { content: 'SR NO.', rowSpan: 2 },
          { content: 'Inq. NO.', rowSpan: 2 },
          { content: 'Profile No.', rowSpan: 2 },
          { content: 'Profile Description', rowSpan: 2 },
          { content: 'Alloy', rowSpan: 2 },
          { content: 'Press Capacity', rowSpan: 2 },
          { content: 'Unit of Measure', rowSpan: 2 },
          { content: 'Quantity', rowSpan: 2 },
          { content: 'TOTAL BASIC AMOUNT(Rs.)', rowSpan: 2 },
          { content: 'GST(Rs.)', colSpan: 2, styles: { halign: 'center' } },
          { content: 'TOTAL(Rs.)', rowSpan: 2 },
        ],
        [{ content: 'SGST (9%)' }, { content: 'CGST (9%)' }],
      ],
      body: bodyRows,
      startY: y,
      theme: 'grid',
      pageBreak: 'auto',
      styles: {
        fontSize: 10,
        fontStyle: 'normal',
        cellPadding: { top: 10, bottom: 10, left: 10, right: 10 },
        lineWidth: 0.9,
        valign: 'middle',
        halign: 'center',
      },
      headStyles: {
        fillColor: [2, 96, 187],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      didParseCell: function (data) {
        if (
          data.cell.raw !== null &&
          !isNaN(data.cell.raw) &&
          data.cell.raw !== ''
        ) {
          data.cell.styles.halign = 'center';
        }
      },
      margin: { left: 10, right: 10 },
      didDrawPage: data => {
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          doc.internal.pageSize.width - 80,
          doc.internal.pageSize.height - 10,
        );
      },
    });
    let finalY = tableResult.lastAutoTable.finalY;
    const remainingSpace = doc.internal.pageSize.height - finalY;
    if (remainingSpace < 200) {
      // Less space? Add new page
      doc.addPage();
      finalY = 10;
    }
    doc.setFontSize(12);
    doc.setFont('WorkSans', 'bold');

    doc.setFillColor(2, 96, 187); // Black background
    doc.rect(420, finalY + 12, 100, 17, 'F'); // Adjust width/height as needed
    doc.setTextColor(255, 255, 255); // White text
    doc.text('Bank Details', 420, finalY + 22);
    y = finalY + 40;

    doc.setTextColor(0, 0, 0);
    const terms = [];

    const bank_details = [
      { label: 'GST No:', value: `${record?.customer?.gstin_number || 'N/A'}` },
      {
        label: 'Bank Name:',
        value: `${record?.customer?.customer_banking_details[0]?.bank_name || 'N/A'}`,
      },
      {
        label: 'Branch:',
        value: `${record?.customer?.customer_banking_details[0]?.bank_branch_address || 'N/A'}`,
      },
      {
        label: 'A/C No:',
        value: `${record?.customer?.customer_banking_details[0]?.bank_account_number || 'N/A'}`,
      },
      {
        label: 'IFSC Code:',
        value: `${record?.customer?.customer_banking_details[0]?.bank_ifsc_code || 'N/A'}`,
      },
    ];

    doc.setFontSize(10);

    const termsX = 8;
    const bankX = doc.internal.pageSize.width / 2 + 120;

    let termsY = y;
    let bankY = y;

    terms.forEach(term => {
      if (term.label.trim()) {
        doc.setFont('WorkSans', 'bold');
        doc.text(term.label, termsX, termsY);
        doc.setFont('WorkSans', 'normal');
        if (term.label === 'Duties Taxes & other Government levies :') {
          doc.text(term.value, termsX + 190, termsY);
        } else if (term.label === 'Minimum Order Quantity:') {
          doc.text(term.value, termsX + 120, termsY);
        } else {
          doc.text(term.value, termsX + 90, termsY);
        }
      } else {
        doc.text(term.value, termsX, termsY);
      }
      termsY += 15;
    });

    bank_details.forEach(field => {
      doc.setFont('WorkSans', 'bold');
      doc.text(field.label, bankX, bankY);
      doc.setFont('WorkSans', 'normal');
      doc.text(field.value, bankX + 80, bankY);
      bankY += 15;
    });
    y += 220;
    const spaceNeededForFooter = 20; // Adjust depending on your footer height
    const lastY = Math.max(termsY, bankY);

    if (lastY + spaceNeededForFooter > pageHeight) {
      doc.addPage();
      y = 40; // Reset y for new page
    } else {
      y = lastY + 20; // Some spacing before footer
    }
    let lastTermsConsitionPositon = 0;
    if (record?.terms_and_condition) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = record.terms_and_condition;

      let termsY = y;
      doc.setFontSize(14);
      doc.setFont('WorkSans', 'bold');
      doc.text(
        'Terms and Conditions',
        doc.internal.pageSize.width / 2,
        termsY,
        {
          align: 'center',
        },
      );
      termsY += 30;
      const processNode = (node, level = 0, indent = 0) => {
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent.trim();
          if (!text) return termsY;
        }

        const textMargin = 10 + indent;
        const maxWidth = doc.internal.pageSize.width - textMargin * 2;
        if (termsY > doc.internal.pageSize.height - 40) {
          doc.addPage();
          termsY = 40;
          lastTermsConsitionPositon = termsY;
        }
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent.trim();
          if (text) {
            doc.setFont('WorkSans', 'normal');
            doc.setFontSize(9);
            const lines = doc.splitTextToSize(
              text,
              doc.internal.pageSize.width - 80,
            );
            doc.text(lines, textMargin, termsY);
            termsY += lines.length * 10 + 5;
            lastTermsConsitionPositon = termsY;
          }
        } else if (node.nodeType === Node.ELEMENT_NODE) {
          switch (node.tagName.toLowerCase()) {
            case 'h3':
              doc.setFont('WorkSans', 'bold');
              doc.setFontSize(11);
              const titleText = node.textContent.trim();
              doc.text(titleText, textMargin, termsY);
              termsY += 15;
              lastTermsConsitionPositon = termsY;
              break;

            case 'p':
              if (node.textContent.trim()) {
                doc.setFont('WorkSans', 'normal');
                doc.setFontSize(9);
                const paragraphText = node.textContent.trim();
                const lines = doc.splitTextToSize(
                  paragraphText,
                  doc.internal.pageSize.width - 80,
                );
                doc.text(lines, textMargin, termsY);
                termsY += lines.length * 10 + 10;
                lastTermsConsitionPositon = termsY;
              }
              break;

            case 'ul':
              termsY += 5;
              for (const child of node.children) {
                if (child.tagName.toLowerCase() === 'li') {
                  const bulletPoint = '• ';
                  doc.setFont('WorkSans', 'normal');
                  doc.setFontSize(9);

                  const bulletWidth =
                    (doc.getStringUnitWidth(bulletPoint) *
                      doc.internal.getFontSize()) /
                    doc.internal.scaleFactor;
                  doc.text(bulletPoint, textMargin, termsY);

                  const itemText = child.textContent.trim();
                  const itemLines = doc.splitTextToSize(
                    itemText,
                    doc.internal.pageSize.width - 90 - bulletWidth,
                  );
                  doc.text(itemLines, textMargin + bulletWidth, termsY);
                  termsY += itemLines.length * 10 + 5;
                  if (termsY > doc.internal.pageSize.height - 40) {
                    doc.addPage();
                    termsY = 40;
                    lastTermsConsitionPositon = termsY;
                  }
                }
              }
              termsY += 5;
              break;

            default:
              for (const child of node.childNodes) {
                processNode(child, level + 1, indent);
              }
          }
        }

        return termsY;
      };
      for (const child of tempDiv.childNodes) {
        processNode(child);
      }
    }
    doc.setFontSize(10);
    doc.setFont('WorkSans', 'bold');
    doc.text('Prepared By', 90, lastTermsConsitionPositon + 40, {
      align: 'right',
    });

    doc.text(
      'Authorised Signatory',
      doc.internal.pageSize.width - 200,
      lastTermsConsitionPositon + 40,
      { align: 'left' },
    );
    doc.text(
      '(Alux ERP)',
      doc.internal.pageSize.width - 220,
      lastTermsConsitionPositon + 50,
      { align: 'left' },
    );
    y += 20;

    /* Last Footer Row */
    const latPageHeight = doc.internal.pageSize.height;
    const footerY = latPageHeight - 20;

    const footerAddress = '110, Ganesh glory, Godrej garden City road, jagatpur Ahmedabad - 382481';
    const footerContact = 'info@aluxerp.com | www.aluxerp.com';

    const pageWidth = doc.internal.pageSize.width;
    const footerY2 = footerY + 9;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(footerAddress, pageWidth / 2, footerY, { align: 'center' });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(footerContact, pageWidth / 2, footerY2, { align: 'center' });

    doc.setDrawColor(0);
    doc.setLineWidth(0.5);
    doc.line(20, footerY - 10, pageWidth - 20, footerY - 10);
    const pdfBlob = doc.output('blob');
    const iframe = document.createElement('iframe');

    iframe.style.position = 'fixed';
    iframe.style.right = '0px';
    iframe.style.bottom = '0px';
    iframe.style.width = '1px';
    iframe.style.height = '1px';
    iframe.style.border = 'none';
    iframe.src = URL.createObjectURL(pdfBlob);

    document.body.appendChild(iframe);

    iframe.onload = () => {
      setTimeout(() => iframe.contentWindow.print(), 500);
    };
  };
  const handlePrintDownloadAll = async () => {
    const pdf = new jsPDF('l', 'pt', 'a4');
    pdf.addFileToVFS('WorkSans-normal.ttf', font);
    pdf.addFont('WorkSans-normal.ttf', 'WorkSans', 'normal');
    pdf.setFont('WorkSans');

    const pdfColumns = [
      { header: 'Sr No.', dataKey: 'SrNo', field: 'SrNo' },
      { header: 'Quotation Date', dataKey: 'quotation_date', field: 'quotation_date' },
      { header: 'Quotation No', dataKey: 'die_quotation_no', field: 'die_quotation_no' },
      { header: 'Customer Name', dataKey: 'customer', field: 'customer' },
      { header: 'Created At', dataKey: 'created_at', field: 'created_at' },
      { header: 'Created By', dataKey: 'created_by', field: 'created_by' },
      { header: 'Updated At', dataKey: 'updated_at', field: 'updated_at' },
      { header: 'Updated By', dataKey: 'updated_by', field: 'updated_by' },
    ];

    const pdffilteredColumns = pdfColumns.filter(column =>
      checkedList.includes(column.dataKey),
    );

    const rows = data.map((item, index) => ({
      SrNo: index + 1,
      quotation_date: item?.quotation_date
        ? dayjs(item.quotation_date).format('DD-MM-YYYY')
        : 'N/A',
      die_quotation_no: item?.die_quotation_no || 'N/A',
      customer: item?.customer?.customer_name || 'N/A',
      created_at: item.created_at ? moment(item.created_at).format('DD-MM-YYYY') : 'N/A',
      created_by: item.created_by ? `${item.created_by.first_name} ${item.created_by.last_name}` : 'N/A',
      updated_at: item.updated_at ? moment(item.updated_at).format('DD-MM-YYYY') : 'N/A',
      updated_by: item.updated_by ? `${item.updated_by.first_name} ${item.updated_by.last_name}` : 'N/A',
    }));

    const currentDateTime = moment().format('DD/MM/YYYY hh:mm:ss A');

    pdf.setFontSize(14);
    pdf.text('Profile Quotation', pdf.internal.pageSize.width / 2, 40, { align: 'center' });

    pdf.autoTable({
      columns: pdffilteredColumns,
      body: rows,
      startY: 65,
      theme: 'grid',
      styles: {
        font: 'times',
        halign: 'center',
        overflow: 'linebreak',
        cellPadding: 3.5,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },
      headStyles: {
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        fillColor: false,
      },
      margin: { top: 65, left: 20, right: 20, bottom: 30 },
      didDrawPage: data => {
        const pageCount = pdf.internal.getNumberOfPages();
        const pageSize = pdf.internal.pageSize;
        const pageWidth = pageSize.width;
        const pageHeight = pageSize.height;

        // Footer: Date & Time (left), Page Number (right)
        pdf.setFontSize(10);
        pdf.text(`${currentDateTime}`, 20, pageHeight - 15);
        pdf.text(`Page ${data.pageNumber}`, pageWidth - 60, pageHeight - 15);

        // Logo only on the first page
        if (data.pageNumber === 1) {
          const imgWidth = 150; // adjust size
          const imgHeight = 40;
          pdf.addImage(logo, 'PNG', pageWidth - imgWidth - 20, 10, imgWidth, imgHeight);
        }
      },
    });

    // Print logic
    pdf.autoPrint();
    const pdfBlob = pdf.output('blob');
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0px';
    iframe.style.bottom = '0px';
    iframe.style.width = '1px';
    iframe.style.height = '1px';
    iframe.style.border = 'none';
    iframe.src = URL.createObjectURL(pdfBlob);

    document.body.appendChild(iframe);

    iframe.onload = () => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    };

    // Log
    dispatch(
      createActivityLog({
        action: 'PRINT',
        module_name: 'Profile Quotation',
        ip_address: ipAddress,
        discription: 'Profile quotation print download successfully!',
      }),
    );
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = 'id';

    if (sortField === 'quotation_date' && sortOrder === 'ascend') {
      sortingEnumKey = 'quotation_date';
    }
    if (sortField === 'quotation_date' && sortOrder === 'descend') {
      sortingEnumKey = '-quotation_date';
    }
    if (sortField === 'die_quotation_no' && sortOrder === 'ascend') {
      sortingEnumKey = 'die_quotation_no';
    }
    if (sortField === 'die_quotation_no' && sortOrder === 'descend') {
      sortingEnumKey = '-die_quotation_no';
    }
    if (sortField === 'customer' && sortOrder === 'ascend') {
      sortingEnumKey = 'customer__customer_name';
    }
    if (sortField === 'customer' && sortOrder === 'descend') {
      sortingEnumKey = '-customer__customer_name';
    }

    return sortingEnumKey;
  };

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
  };
  const handlePageSizeChange = (current, size) => {
    setPage(1); // Reset to first page if needed
    setPageSize(size); // You must have a setter or state update
  };

  const handlePageChange = newPage => {
    setPage(newPage);
    setOrderBy('');
  };

  const dynamicColumns = columns.filter(column =>
    checkedList.includes(column.dataIndex || column.key),
  );
  const activityLog = async () => {
    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Die Quotation',
        ip_address: ipAddress,
        discription: 'Die Quotation excel download successfully!',
      }),
    );
  };
  return (
    <div className="mainwrap">
      <div className="sticyaction">
        <Row
          className="pagenamerow mb-0"
          justify="space-between"
          align="middle"
        >
          <Col>
            <h2>Die Quotations</h2>
            <div className="bredcrumbwrp">
              <Link
                className="back"
                onClick={() => {
                  navigate(-1);
                }}
              >
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/dashboard">Home</Link> },
                  { title: 'Die Quotations' },
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space size="small">
              <Input
                placeholder="Search"
                className="searchbox"
                prefix={<SearchOutlined />}
                value={inputValue}
                style={{ width: 190 }}
                onChange={handleSearch}
              />
              <Button
                type="primary"
                disabled={!search && !orderBy}
                className="iconlink"
                onClick={resetFilter}
              >
                <FilterIcon />
              </Button>
              {currentUser.permissions.permissions.die_quotation.includes(
                'download_die_quotation_excel_copy',
              ) && (
                  <Tooltip
                    placement="top"
                    title={'Export Excel'}
                    onClick={activityLog}
                  >
                    <Button
                      type="primary"
                      className="iconlink"
                      style={{ width: '10px' }}
                    >
                      <CSVLink
                        filename={'die_quotation.csv'}
                        className="iconlink"
                        style={{ width: '10px' }}
                        data={csvData}
                        headers={csvFilterheaders}
                      >
                        <FileExcelOutlined />
                      </CSVLink>
                    </Button>
                  </Tooltip>
                )}
              {currentUser.permissions.permissions.die_quotation.includes(
                'print_die_quotation_pdf_copy',
              ) && (
                  <Button
                    type="primary"
                    onClick={handlePrintDownloadAll}
                    className="iconlink"
                    icon={<PrintIcon />}
                  ></Button>
                )}
              {currentUser.permissions.permissions.die_quotation.includes(
                'add_diequotation',
              ) && (
                  <Link to="/add-die-quotation">
                    <Tooltip title=" Add Die Quotation">
                      <Button type="primary" icon={<PlusOutlined />}>
                        Add Die Quotation
                      </Button>
                    </Tooltip>
                  </Link>
                )}
              <Link to="/archive-die-quotation-list" className="iconlink">
                <Tooltip title="Archive">
                  <Archiveicon />
                </Tooltip>
              </Link>
              <div className="gridiconswrap">
                <Tooltip placement="topLeft" title="Column Filter Options">
                  <Dropdown
                    overlay={columnMenu}
                    trigger={['click']}
                    placement="bottomRight"
                    visible={dropdownVisible}
                    onVisibleChange={setDropdownVisible}
                  >
                    <Button
                      type="primary"
                      icon={<ColumnList />}
                      onClick={e => e.preventDefault()}
                    />
                  </Dropdown>
                </Tooltip>
              </div>
            </Space>
          </Col>
        </Row>
      </div>

      <div className="tablewrp">
        <Table
          rowKey="id"
          // rowSelection={rowSelection}
          columns={dynamicColumns}
          dataSource={dataSource}
          pagination={false} // Disable built-in pagination
          loading={status === 'loading'}
          scroll={{ x: '600px' }}
          onChange={handleTableChange}
        />
      </div>

      <Pagination
        current={page}
        pageSize={pagesize}
        total={totalRecord}
        showSizeChanger
        onShowSizeChange={handlePageSizeChange}
        pageSizeOptions={['20', '50', '100']}
        showTotal={total => `Total ${total} items`}
        onChange={handlePageChange}
        locale={{ items_per_page: 'Items / Page' }}
      />
    </div>
  );
}

export default DieQuotationList;
